import React, { useMemo, useState } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import { simpleHash } from "../../../../utils/string-helpers";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";

const HandleMealPlanTemplateModal = ({ locals }) => {
  const { query, mealPlanTemplates = [], activeUserToRequest, mealPlan } = locals;
  const { templateId, planMode = "detailed", saveAsTemplate, dayType, optionId } = query;
  const { t } = useTranslation(["coaching-portal", "common"]);
  const coachId = activeUserToRequest?._id;
  const currentTemplate = mealPlanTemplates.find(
    ({ _id }) => _id === templateId
  ) || {
    title: "",
    description: "",
  };
  const {
    title: currentTemplateTitle,
    description: currentTemplateDescription,
  } = currentTemplate;
  let titleModal;
  if (templateId) {
    titleModal = t("handleMealPlanTemplateModal.editMealPlanTemplateTitle");
  } else if (planMode === "simplified") {
    titleModal = t("handleMealPlanTemplateModal.newSingleDayMealPlanTitle");
  } else {
    titleModal = t("handleMealPlanTemplateModal.newMultiDayMealPlanTitle");
  }

  const actionText = templateId ? t("common:edit") : t("handleMealPlanTemplateModal.create");
  const buttonText =
    planMode === "simplified"
      ? t("handleMealPlanTemplateModal.singleDayMealPlanButton", { action: actionText })
      : t("handleMealPlanTemplateModal.multiDayMealPlanButton", { action: actionText });

  const initialState = {
    title: currentTemplateTitle || "",
    description: currentTemplateDescription || "",
  };

  const [state, setState] = useState(initialState);

  const { title, description, numberOfWeeks } = state;

  const weeksOptions = Array.from({ length: 12 }, (_, i) => ({
    label: i + 1,
    value: i + 1,
  }));

  const isButtonDisabled = useMemo(() => {
    if (title.length === 0) return true;
    if (!templateId) {
      // For simplified plans, numberOfWeeks is not required
      if (planMode === "simplified") return false;
      // For detailed plans, numberOfWeeks is required
      return !numberOfWeeks;
    }
    return false;
  }, [title, templateId, numberOfWeeks, planMode]);
  
  /**
   * Update the state
   * @param {*} newState
   * @returns
   */
  const updateState = (newState) =>
    setState((prevState) => ({ ...prevState, ...newState }));

  /**
   * Clean meal plan data for template creation
   * Removes macrosTotal, mealId, and ensures foodId is only the ID (not the full object)
   * @param {*} meals - Array of meals to clean
   * @returns {Array} Cleaned meals array
   */
  const cleanMealsForTemplate = (meals) => {
    if (!Array.isArray(meals)) return [];
    
    return meals.map((meal) => {
      const cleanedMeal = {
        type: meal.type,
        order: meal.order,
      };

      // Clean foods array
      if (Array.isArray(meal.foods) && meal.foods.length > 0) {
        cleanedMeal.foods = meal.foods.map((food) => {
          const cleanedFood = {
            type: food.type,
            quantityNumber: food.quantityNumber,
            unitOfMeasure: food.unitOfMeasure,
            completed: false, // Always false for templates
          };

          // Extract foodId - if it's an object, get the _id or id, otherwise use it directly
          if (food.foodId) {
            if (typeof food.foodId === "object" && food.foodId !== null) {
              cleanedFood.foodId = food.foodId._id || food.foodId.id || food.foodId;
            } else {
              cleanedFood.foodId = food.foodId;
            }
          }

          // Include mealId if present (for custom meals)
          if (food.mealId) {
            // Extract mealId - if it's an object, get the _id, otherwise use it directly
            if (typeof food.mealId === "object" && food.mealId !== null) {
              cleanedFood.mealId = food.mealId._id || food.mealId.id || food.mealId;
            } else {
              cleanedFood.mealId = food.mealId;
            }
          }

          return cleanedFood;
        });
      } else {
        cleanedMeal.foods = [];
      }

      return cleanedMeal;
    });
  };

  /**
   * Create a template with the same meal plan information
   */
  const handleSubmit = () => {
    const isEditing = Boolean(templateId);

    if (isEditing) {
      // Editing: use update endpoint
    const body = {
      title: String(title),
      description: String(description),
    };
      const url = `/actions/meal-planner/update-meal-plan?mealPlanId=${templateId}&proxyAthlete=${undefined}`;
    submit(url, {
      method: "POST",
      body,
    });
      return;
    }

    // Creating new template
    if (planMode === "detailed") {
      // Detailed: use create-new-template endpoint
      const body = {
        title: String(title),
        description: String(description),
        numberOfWeeks: parseInt(numberOfWeeks),
      };
      body.requestId = simpleHash(JSON.stringify(body));
      submit("/actions/meal-planner/create-new-template", {
        method: "POST",
        body,
      });
    } else {
      // Simplified: use create-meal-plan endpoint
      const defaultMeals = [
        { type: "Breakfast", order: 1, foods: [] },
        { type: "Lunch", order: 2, foods: [] },
        { type: "Snack", order: 3, foods: [] },
        { type: "Dinner", order: 4, foods: [] },
      ];

      // If saving as template from an existing option, use that option's data
      let trainingDayOption = {
        title: "Option 1",
        meals: defaultMeals,
      };

      if (saveAsTemplate && mealPlan && optionId && dayType) {
        // Get the selected option from the meal plan
        const optionsArray = dayType === "training" 
          ? mealPlan.trainingDayOptions || []
          : mealPlan.restDayOptions || [];
        
        const selectedOption = optionsArray.find(
          (opt) => opt.optionId?.toString() === optionId?.toString()
        );
        if (selectedOption) {
          // Clean the meals data before using it for template
          const cleanedMeals = cleanMealsForTemplate(selectedOption.meals);
          // Use the selected option's title and cleaned meals
          trainingDayOption = {
            title: selectedOption.title || "Option 1",
            meals: cleanedMeals.length > 0 ? cleanedMeals : defaultMeals,
          };
        }
      }

      const body = {
        planMode: "simplified",
        title: String(title),
        description: String(description),
        published: false,
        coachId,
        type: "template",
        // Flag to indicate this template is being created from "save as template" action
        // This helps determine the redirect behavior after creation
        ...(saveAsTemplate && { fromSaveAsTemplate: true }),
        // NOTE: For simplified meal plan templates, only trainingDayOptions is used for editing.
        // The tabs (Training Day / Rest Day) are hidden in the UI, and only trainingDayOptions[0]
        // can be edited. restDayOptions[0] is created with default structure but remains empty.
        trainingDayOptions: [trainingDayOption],
        restDayOptions: [
          {
            title: "Option 1",
            meals: defaultMeals,
          },
        ],
      };

      body.requestId = simpleHash(JSON.stringify(body));
      submit("/actions/meal-planner/create-meal-plan", {
        method: "POST",
        body,
      });
    }
  };

  /**
   * Get the input values
   * @param {*} e
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({ [name]: value });
  };

  return (
    <Modal
      className="handle-meal-plan-template"
      title={titleModal}
      toClear={["templateId"]}
    >
      <div className="body">
        <div className="inputs">
          <div>
            <label className="label" htmlFor="title">
              {t("handleMealPlanTemplateModal.planNameLabel")}
            </label>
            <input
              type="text"
              placeholder={t("handleMealPlanTemplateModal.planNamePlaceholder")}
              name="title"
              value={title}
              onChange={handleInputChange}
              maxLength={60}
            />
          </div>
          <div>
            <label className="label" htmlFor="description">
              {t("handleMealPlanTemplateModal.descriptionLabel")}
            </label>
            <textarea
              name="description"
              className="textarea-input"
              placeholder={t("handleMealPlanTemplateModal.descriptionPlaceholder")}
              value={description}
              onChange={handleInputChange}
              maxLength={100}
            ></textarea>
          </div>
          {!templateId && planMode !== "simplified" && (
            <div>
              <label>{t("handleMealPlanTemplateModal.numberOfWeeksLabel")}</label>

              <SearchDropdown
                name="weeks"
                options={weeksOptions}
                handleChange={({ value }) =>
                  updateState({ numberOfWeeks: value })
                }
                value={numberOfWeeks}
                valueKey="label"
              />
            </div>
          )}
        </div>

        <div className="buttons justify-end">
          <button
            data-button-type="primary"
            onClick={handleSubmit}
            disabled={isButtonDisabled}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default HandleMealPlanTemplateModal;

HandleMealPlanTemplateModal.propTypes = {
  locals: PropTypes.object,
};
